/**
 * Platform-aware hotkey labels: "⌥⇧R" on macOS and iOS, "Alt+Shift+R" everywhere else (DS
 * readme, "Hotkeys"). This is the one place clients turn a key combination into a label; never
 * hard-code the Mac glyphs in a component.
 *
 * Detection reads `navigator` only, which is why one helper serves every client unchanged:
 * - the web app in a browser;
 * - the Tauri desktop app (SD7), whose webview reports the host OS through the same fields:
 *   WKWebView on macOS says "MacIntel", WebView2 on Windows "Win32", WebKitGTK on Linux
 *   "Linux x86_64";
 * - extension pages (popup, side panel), which are ordinary Chrome documents.
 * No Tauri OS plugin and no chrome.* API is needed, and a page without `navigator` (SSR, Node
 * tests) gets the non-Mac labels.
 *
 * Order of sources: `navigator.userAgentData.platform` (Chromium; "macOS"), then the legacy
 * `navigator.platform` ("MacIntel", "iPhone", "iPad"), then the user-agent string. iPadOS
 * Safari can report "MacIntel"; it is a Mac-glyph platform either way.
 */

export type HotkeyPlatform = "mac" | "other";

/** The parts of `navigator` that detection reads; tests pass a stub. */
export type NavigatorLike = Partial<Pick<Navigator, "platform" | "userAgent">> & {
  userAgentData?: { platform?: string } | null;
};

const MAC_RE = /mac|iphone|ipad|ipod/i;

export function detectPlatform(nav?: NavigatorLike | null): HotkeyPlatform {
  const n: NavigatorLike | undefined =
    nav === undefined ? (typeof navigator === "undefined" ? undefined : (navigator as NavigatorLike)) : nav ?? undefined;
  if (!n) return "other";
  const source = n.userAgentData?.platform || n.platform || n.userAgent || "";
  return MAC_RE.test(source) ? "mac" : "other";
}

/** "Mod" is ⌘ on a Mac and Ctrl elsewhere (the usual cross-platform primary modifier). */
export type HotkeyModifier = "Mod" | "Alt" | "Shift" | "Ctrl";

const MAC_GLYPHS: Record<HotkeyModifier, string> = { Mod: "⌘", Ctrl: "⌃", Alt: "⌥", Shift: "⇧" };
const OTHER_NAMES: Record<HotkeyModifier, string> = { Mod: "Ctrl", Ctrl: "Ctrl", Alt: "Alt", Shift: "Shift" };

function isModifier(key: string): key is HotkeyModifier {
  return key === "Mod" || key === "Alt" || key === "Shift" || key === "Ctrl";
}

/**
 * Formats a key combination for display, e.g. `formatHotkey(["Alt", "Shift", "R"])` is "⌥⇧R"
 * on a Mac (glyphs, no separators) and "Alt+Shift+R" elsewhere. Pass the keys in the order a
 * person presses them; the platform defaults to the running one.
 */
export function formatHotkey(
  keys: readonly (HotkeyModifier | (string & {}))[],
  platform: HotkeyPlatform = detectPlatform(),
): string {
  if (platform === "mac") return keys.map((k) => (isModifier(k) ? MAC_GLYPHS[k] : k)).join("");
  return keys.map((k) => (isModifier(k) ? OTHER_NAMES[k] : k)).join("+");
}
